import { Box, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

// Maps the per-part resource consumption status coming from the reportable-parts endpoint to one of
// three visual states. Anything unknown/missing falls back to 'none' so the boxes keep their
// current neutral look until the backend ships the field.
const normalizeStatus = (part) => {
  const raw = (part?.resourceConsumptionStatus ?? '').toString().toUpperCase()
  if (raw === 'COMPLETE') return 'complete'
  if (raw === 'PARTIAL') return 'partial'
  return 'none'
}

// Horizontal row of selectable boxes, one per part of the active job at this node — the ones still
// pending first, then the ones already reported, which stay selectable and writable. The operator
// picks one part at a time; the material quantities entered below are attributed to it.
// Colour per box reflects how much of the part's resources are already mounted at this node:
//   - complete (all resources mounted)      -> green
//   - partial  (some mounted, not all)      -> orange
//   - none     (nothing mounted yet)        -> neutral (current look)
// A small dot marks parts with unconfirmed local input (quantities queued but not yet posted).
const PartSelectorBar = ({ parts = [], selectedPartId = 0, onSelect, partsWithQty = {}, t_ = (k) => k }) => {
  if (parts.length === 0) {
    return (
      <Box sx={{ px: 1, py: 1 }}>
        <Typography variant='caption' color='text.secondary'>{t_('No reportable parts')}</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
        {t_('Parts')}
      </Typography>
      <Stack direction='row' spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
        {parts.map((part) => {
          const selected = part.id === selectedPartId
          const hasQty = partsWithQty[part.id] === true
          const status = normalizeStatus(part)
          return (
            <Box
              key={part.id}
              role='button'
              aria-pressed={selected}
              data-status={status}
              onClick={() => onSelect(part.id)}
              sx={(theme) => {
                const statusBorder = status === 'complete'
                  ? theme.palette.success.main
                  : status === 'partial' ? theme.palette.warning.main : theme.palette.divider
                const statusBg = status === 'complete'
                  ? alpha(theme.palette.success.main, 0.18)
                  : status === 'partial' ? alpha(theme.palette.warning.main, 0.18) : theme.palette.background.default
                return {
                  position: 'relative',
                  flex: '0 0 auto',
                  minWidth: 96,
                  maxWidth: 200,
                  cursor: 'pointer',
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  border: '2px solid',
                  borderColor: selected ? theme.palette.primary.main : statusBorder,
                  bgcolor: statusBg,
                  // Selection ring on top of the status colour, so both stay visible at once.
                  boxShadow: selected ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.4)}` : 'none',
                  '&:hover': { borderColor: selected ? theme.palette.primary.main : theme.palette.text.secondary }
                }
              }}
            >
              <Typography variant='subtitle2' sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                {part.code}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {part.name}
              </Typography>
              {hasQty && (
                <Box sx={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main' }} />
              )}
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

export default PartSelectorBar
